import { Form, Input, Button, Space, message } from "antd";
import Modal from "../../../components/generic/Modal";
import { useCallback, useEffect, useMemo, useState } from "react";
import Select from "../../../components/generic/Select";
import { parseError } from "../../../utils/shared";
import httpClient from "../../../api/httpClient";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { selectSelectedTenantId } from "../../../store/user/selectors";
import { editEmailRouting } from "../../../store/branch/thunks";

const EmailRoutingModal = ({ onCancel, selectedBranch, topics }) => {
  const selectedTenantId = useSelector(selectSelectedTenantId);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [isUsernameAvailable, setIsUsernameAvailable] = useState(null);
  const isEdit = !!selectedBranch?.email_routing?.id;
  const dispatch = useDispatch();

  useEffect(() => {
    if (isEdit) {
      form.setFieldsValue(selectedBranch?.email_routing);
    }
  }, [isEdit]);

  const topicsOptions = useMemo(() => {
    const options =
      topics
        ?.filter((t) => t?.branch_ids?.includes(selectedBranch?.id))
        ?.map(({ name, id }) => ({
          label: name,
          value: id,
        })) || [];

    return options;
  }, [topics, selectedBranch]);

  const onCheckUsername = useCallback(async () => {
    try {
      const username = form.getFieldValue("username");

      await form.validateFields(["username"]);

      setCheckingUsername(true);

      const { data } = await httpClient.post(
        "conversations/username/available",
        {
          username,
        },
      );

      setIsUsernameAvailable(data?.is_available);

      if (data?.is_available) {
        message.success("Username is available");
      } else {
        message.error("Username is not available");
      }
    } catch (error) {
      parseError(error, true);
    } finally {
      setCheckingUsername(false);
    }
  }, [form]);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        setSubmitting(true);
        const { payload } = await dispatch(
          editEmailRouting({
            ...values,
            tenant_id: selectedTenantId,
            branch_id: selectedBranch?.id,
          }),
        );

        if (payload?.data?.tenant_id) onCancel();
      } finally {
        setSubmitting(false);
      }
    },
    [onCancel],
  );

  return (
    <Modal
      isProcessing={submitting}
      title={`Configure Email Routing for ${selectedBranch?.name}`}
      onCancel={onCancel}
      width={600}
      onOk={() => form.submit()}
      open
      footer={isEdit || isUsernameAvailable ? undefined : null}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={submitting || isEdit}
      >
        <Form.Item
          label="Username"
          name="username"
          rules={[
            {
              required: true,
              message: "Please enter username",
            },
          ]}
        >
          <Input
            placeholder="Enter Username"
            suffix="@mail.kelu.uk"
            onChange={() => {
              if (isUsernameAvailable) setIsUsernameAvailable(null);
            }}
          />
        </Form.Item>

        {!isEdit && (
          <Space style={{ marginBottom: 24 }}>
            <Button
              type="primary"
              loading={checkingUsername}
              disabled={checkingUsername}
              onClick={onCheckUsername}
            >
              Check Availability
            </Button>

            {isUsernameAvailable && (
              <span style={{ color: "green" }}>
                <CheckCircleOutlined /> Username Available
              </span>
            )}

            {isUsernameAvailable != null && !isUsernameAvailable && (
              <span style={{ color: "red" }}>
                <CloseCircleOutlined /> Username Not Available
              </span>
            )}
          </Space>
        )}

        {(isUsernameAvailable || isEdit) && (
          <>
            <Form.Item
              name="topic_id"
              label="Topics"
              rules={[
                {
                  required: true,
                  message: "Please select topic",
                },
              ]}
            >
              <Select placeholder="Select topics" options={topicsOptions} />
            </Form.Item>

            <Form.Item
              label="Display Name"
              name="display_name"
              rules={[
                {
                  required: true,
                  message: "Please input Display Name",
                },
              ]}
            >
              <Input placeholder="Enter Display Name" />
            </Form.Item>

            <Form.Item
              label="First Name"
              name="first_name"
              rules={[
                {
                  required: true,
                  message: "Please input First Name",
                },
              ]}
            >
              <Input placeholder="Enter First Name" />
            </Form.Item>

            <Form.Item
              label="Last Name"
              name="last_name"
              rules={[
                {
                  required: true,
                  message: "Please input Last Name",
                },
              ]}
            >
              <Input placeholder="Enter Last Name" />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default EmailRoutingModal;
